import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  CreateMessageRequest,
  DeleteMessageRequest,
  DeleteMessageResponse,
  ViewMessageOverviewRequest,
  ViewMessageOverviewResponse,
  ViewMessagesRequest,
  ViewMessagesResponse
} from './support.types';

import { DELETING_MESSAGES, RETRIEVING_MESSAGES, RETRIEVING_MESSAGE_OVERVIEW } from './support.constants';

import {
  requestCreateMessage,
  requestDeleteMessage,
  requestViewMessageOverview,
  requestViewMessages
} from './support.actions';

import {
  getCreateMessageRequest,
  getDeleteMessageRequest,
  getViewMessageOverviewRequest,
  getViewMessagesRequest
} from './support.endpoints';

import { setMessageOverviews, setMessageOwner, setMessages } from '../../slices/programme/support/support.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performViewMessageOverviewRequest({ payload }: SagaPayload<ViewMessageOverviewRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    if (requestData.showSpinner) {
      yield put(addSpinner(RETRIEVING_MESSAGE_OVERVIEW));
    }

    const [endpoint, options] = getViewMessageOverviewRequest(requestData);

    const { data }: AxiosResponse<ViewMessageOverviewResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setMessageOverviews(data.overviewSupportMessages));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_MESSAGE_OVERVIEW));
  }
}

export function* watchForViewMessageOverviewRequest() {
  yield takeLatest(requestViewMessageOverview.type, performViewMessageOverviewRequest);
}

export function* performViewMessagesRequest({ payload }: SagaPayload<ViewMessagesRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    if (requestData.showSpinner) {
      yield put(addSpinner(RETRIEVING_MESSAGES));
    }

    const [endpoint, options] = getViewMessagesRequest(requestData);

    const { data }: AxiosResponse<ViewMessagesResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setMessages(data.supportMessages));
    yield put(setMessageOwner(data.ownerSystemUserName));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_MESSAGES));
  }
}

export function* watchForViewMessagesRequest() {
  yield takeLatest(requestViewMessages.type, performViewMessagesRequest);
}

export function* performCreateMessageRequest({ payload }: SagaPayload<CreateMessageRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    const [endpoint, options] = getCreateMessageRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForCreateMessageRequest() {
  yield takeLatest(requestCreateMessage.type, performCreateMessageRequest);
}

export function* performDeleteMessageRequest({ payload }: SagaPayload<DeleteMessageRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(DELETING_MESSAGES));
    const [endpoint, options] = getDeleteMessageRequest(requestData);

    const { data }: AxiosResponse<DeleteMessageResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewMessageOverview({
        requestData: { programmeExternalReference: requestData.programmeExternalReference }
      })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DELETING_MESSAGES));
  }
}

export function* watchForDeleteMessageRequest() {
  yield takeLatest(requestDeleteMessage.type, performDeleteMessageRequest);
}

export default function* supportSaga() {
  yield all([
    watchForViewMessageOverviewRequest(),
    watchForViewMessagesRequest(),
    watchForCreateMessageRequest(),
    watchForDeleteMessageRequest()
  ]);
}
